// Sugalarm — Portrait Clock Face (cycle 253).
//
// Replaces the cycle-157 "rotate your phone" prompt on the mobile
// Alarm Clock Mode in PORTRAIT orientation with a real vertical clock
// face: glucose at the top, a large zone-colored rounded clock in the
// centre, and the reused collapsed `BolusRings` at the bottom — all
// floating over an animated blue-wave canvas on the forced-midnight
// ground. Mounted only by `AlarmClockMode` when `!isLandscape`; the
// landscape face and every alarm overlay are byte-identical.
//
// PURELY PRESENTATIONAL — owns no alarm logic. Zone color + staleness
// are computed by the parent and passed in (so the clock + glucose
// track the threshold zone together, neutral grey when stale, exactly
// like the landscape face).

import { ClockFaceWaves } from "./ClockFaceWaves";
import { BolusRings } from "../glucose/BolusRings";
import { TrendArrow } from "../glucose/TrendArrow";
import type { BasalContext, BolusForRings } from "../../lib/iob";
import {
  displayValue,
  fmtAge,
  zoneLabel,
  zoneOf,
} from "../../lib/glucose";
import type { DisplayGlucose } from "../../lib/currentGlucose";
import type { Thresholds } from "../../lib/types";
import styles from "./PortraitClockFace.module.css";

// The same exit glyph the landscape face uses (cycle-149 `ExitGlyph`).
function ExitGlyph() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M15 4h4a1 1 0 0 1 1 1v14a1 1 0 0 1-1 1h-4M10 17l-5-5 5-5M5 12h11"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

type Zone = ReturnType<typeof zoneOf>;

interface Props {
  /** Zone color string (e.g. `var(--zone-ok)`) — neutral grey when stale.
   *  Drives BOTH the glucose value and the clock, computed by the parent. */
  color: string;
  zone: Zone;
  noData: boolean;
  stale: boolean;
  /** Cycle 435: the number is a selector-released `DisplayGlucose` or null. */
  current: DisplayGlucose | null;
  units: "mg/dL" | "mmol/L";
  trend: { glyph: string; label: string };
  deltaStr: string | null;
  ageMin: number;
  thresholds: Thresholds;
  /** Clock pieces (the parent already ticks `useNow(1000)`). */
  hh: number;
  mm: string;
  ss: string;
  ampm: string;
  h24: boolean;
  dateLine: string;
  /** Whole-face night dim (auto-derived by the controller). */
  dim: boolean;
  /** IOB rings — gated on `showRings` (rings enabled AND insulin on board). */
  showRings: boolean;
  boluses: BolusForRings[];
  basal: BasalContext | null;
  now: number;
  /** Cycle 452.2: a resting Low/High strip is pinned to the bottom edge —
   *  lift the content and the exit control above its band. */
  reserveBottomStrip?: boolean;
  /** Cycle 452.2: a collapsed urgent strip sits in flow at the top. */
  reserveTopStrip?: boolean;
  onExit: () => void;
}

export function PortraitClockFace({
  color,
  zone,
  noData,
  stale,
  current,
  units,
  trend,
  deltaStr,
  ageMin,
  thresholds,
  hh,
  mm,
  ss,
  ampm,
  h24,
  dateLine,
  dim,
  showRings,
  boluses,
  basal,
  now,
  reserveBottomStrip = false,
  reserveTopStrip = false,
  onExit,
}: Props) {
  const dimColor = `color-mix(in srgb, ${color} 65%, var(--bg))`;

  return (
    <div
      className={`${styles.root} ${dim ? styles.dimmed : ""} ${
        reserveBottomStrip ? styles.reserveBottom : ""
      } ${reserveTopStrip ? styles.reserveTop : ""}`}
    >
      <ClockFaceWaves className={styles.canvas} />

      <div
        className={`${styles.content} ${
          showRings ? "" : styles.contentCentered
        }`}
      >
        {/* TOP — glucose */}
        <div className={styles.glucoseSection}>
          <div className={styles.eyebrow} style={{ color }}>
            <span className={styles.eyebrowDot} style={{ background: color }} />
            {noData ? "No data" : zoneLabel(zone)}
          </div>
          <div className={styles.glucoseRow}>
            <span className={`${styles.glucoseNum} tnum`} style={{ color }}>
              {noData || !current ? "—" : displayValue(current.sgv, units)}
            </span>
            {!noData && (
              <span className={styles.trendUnit}>
                <TrendArrow
                  glyph={trend.glyph}
                  zone={zone}
                  stale={stale}
                  size="22px"
                />
                <span className={styles.unitLabel} style={{ color }}>
                  {units}
                </span>
              </span>
            )}
          </div>
          <div className={styles.statusLine}>
            {noData ? (
              "Please check the CGM."
            ) : (
              <span className="tnum">
                {trend.label}
                {deltaStr !== null ? ` · ${deltaStr} ${units}` : ""}
                {current ? ` · ${fmtAge(ageMin)}` : ""}
                {" · Target "}
                {displayValue(thresholds.target_low, units)}–
                {displayValue(thresholds.target_high, units)}
              </span>
            )}
          </div>
        </div>

        {/* CENTRE — clock hero */}
        <div className={styles.clockSection}>
          <div className={styles.clockRow}>
            <span
              className={`${styles.clockHHMM} ${
                String(hh).length > 1 ? styles.clockHHMMWide : ""
              } tnum`}
              style={{ color }}
            >
              {hh}:{mm}
            </span>
            <span className={styles.clockAccents}>
              <span
                className={`${styles.clockSeconds} tnum`}
                style={{ color: dimColor }}
              >
                :{ss}
              </span>
              {!h24 && (
                <span className={styles.clockAmPm} style={{ color: dimColor }}>
                  {ampm}
                </span>
              )}
            </span>
          </div>
          <div className={styles.dateLine}>{dateLine}</div>
        </div>

        {/* BOTTOM — IOB rings (only when insulin is on board) */}
        {showRings ? (
          <div className={styles.ringsSection}>
            <div className={styles.ringsGlass}>
              <BolusRings
                boluses={boluses}
                now={now}
                basal={basal}
                hideContext
                bare
              />
            </div>
          </div>
        ) : null}
      </div>

      <button
        type="button"
        className={styles.exitBtn}
        aria-label="Exit clock mode"
        onClick={onExit}
      >
        <ExitGlyph />
      </button>
    </div>
  );
}
